/**
 * Customer self-scheduling over the PUBLIC droom booking seam (SPEC-07 §9 /
 * F-CA-09; be `DroomBookingController`). The session link IS the credential
 * (SPEC-01 R4 — customers are never IAM subjects): every path is
 * session-scoped, targets resolve server-side, and the grid is the SAFE
 * subset only — slotStart/slotEnd/bookable, never agent identities or tenant
 * capacity. Bookable band: INITIATED/SCHEDULED only (queued/live ⇒ 422 —
 * render as "your session has moved on", never an error tone).
 */

import { request } from './client';

/** One customer-visible grid cell — the safe subset, nothing more. */
export interface DroomSlotCell {
  slotStart: string;
  slotEnd: string;
  bookable: boolean;
}

export interface DroomSlotGrid {
  from: string;
  to: string;
  slotLengthMinutes: number;
  cells: DroomSlotCell[];
}

/** The session's booking as the customer may see it. */
export interface DroomBooking {
  id?: string;
  state: string;
  slotStart: string;
  slotEnd: string;
  customerTz: string;
  holdExpiresAt?: string | null;
}

export function getDroomSlots(
  sessionId: string,
  fromIso: string,
  toIso: string,
): Promise<DroomSlotGrid> {
  const q = new URLSearchParams({ from: fromIso, to: toIso });
  return request<DroomSlotGrid>(
    `/api/v1/sessions/${encodeURIComponent(sessionId)}/booking/slots?${q.toString()}`,
  );
}

/** The session's own booking (re-entry read) — 404 when none exists yet. */
export function getDroomBooking(sessionId: string): Promise<DroomBooking> {
  return request<DroomBooking>(`/api/v1/sessions/${encodeURIComponent(sessionId)}/booking`);
}

/** Hold a slot (server picks the agent) — 201 HELD, or 409 with a refreshed grid. */
export function holdDroomSlot(
  sessionId: string,
  slotStart: string,
  customerTz: string,
): Promise<DroomBooking> {
  return request<DroomBooking>(`/api/v1/sessions/${encodeURIComponent(sessionId)}/booking:hold`, {
    method: 'POST',
    body: { slotStart, customerTz },
  });
}

/** Idempotent confirm — replaying the SAME key returns the same 200. */
export function confirmDroomBooking(
  sessionId: string,
  idempotencyKey: string,
): Promise<DroomBooking> {
  return request<DroomBooking>(`/api/v1/sessions/${encodeURIComponent(sessionId)}/booking:confirm`, {
    method: 'POST',
    body: { idempotencyKey },
  });
}

/** Stamp a reschedule request on the confirmed booking (the re-pick is hold+confirm). */
export function requestDroomReschedule(
  sessionId: string,
  reason: string,
): Promise<DroomBooking> {
  return request<DroomBooking>(
    `/api/v1/sessions/${encodeURIComponent(sessionId)}/booking:reschedule-request`,
    { method: 'POST', body: { reason } },
  );
}
